import React, { useState, useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, PointElement, LineElement,
  Filler,
  Tooltip as ChartTooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { ArrowRight } from 'lucide-react';
import { useUserData } from '../context/UserDataContext';
import { runMonteCarlo } from '../utils/monteCarlo';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, ChartTooltip, Legend);

const fmt  = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
const fmtC = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', notation: 'compact', maximumFractionDigits: 1 }).format(n);

const HORIZONS = [
  { label: '5y',  years: 5  },
  { label: '10y', years: 10 },
  { label: '20y', years: 20 },
  { label: '30y', years: 30 },
];

const SCENARIOS = [
  { key: 'conservative', label: 'Conservative', return: 5,  vol: 10, color: '#94a3b8' },
  { key: 'moderate',     label: 'Moderate',     return: 7,  vol: 15, color: '#60a5fa' },
  { key: 'aggressive',   label: 'Aggressive',   return: 10, vol: 22, color: '#34d399' },
];

// ── Sub-components ─────────────────────────────────────────────────────────────
function OutcomeCard({ label, pessimistic, median, optimistic, color }) {
  return (
    <div className="flex-1 bg-slate-700/40 rounded-xl p-3.5 border border-slate-600/40">
      <div className="text-xs text-slate-500 mb-2">{label}</div>
      <div className="text-base font-bold tabular-nums" style={{ color }}>{fmtC(median)}</div>
      <div className="text-[11px] text-slate-500 mt-1 space-y-0.5">
        <div>Bear: {fmtC(pessimistic)}</div>
        <div>Bull: {fmtC(optimistic)}</div>
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function InvestmentProjection({ totalValue, monthlyContrib = 0 }) {
  const { budgetData } = useUserData();
  const [horizonIdx,  setHorizonIdx]  = useState(1); // default 10y
  const [scenarioKey, setScenarioKey] = useState('moderate');

  // Derive monthly contribution from budget savings if not passed
  const contrib = useMemo(() => {
    if (monthlyContrib > 0) return monthlyContrib;
    const income = budgetData?.income || 0;
    if (income <= 0) return 0;
    const txns = budgetData?.transactions || [];
    const cats  = budgetData?.categories  || [];
    let spent = 0;
    if (txns.length > 0) {
      const now = new Date();
      const mStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const mEnd   = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
      spent = txns
        .filter(t => { const d = new Date(t.date + 'T12:00:00'); return t.kind === 'expense' && d >= mStart && d <= mEnd; })
        .reduce((s, t) => s + t.amount, 0);
    } else {
      spent = cats.reduce((s, c) => s + (c.spent || 0), 0);
    }
    return Math.max(0, income - spent);
  }, [monthlyContrib, budgetData]);

  const scenario = SCENARIOS.find(s => s.key === scenarioKey);
  const years    = HORIZONS[horizonIdx].years;

  // Run full simulation for selected scenario + horizon
  const simResult = useMemo(() => {
    if (totalValue <= 0 && contrib <= 0) return null;
    return runMonteCarlo({
      currentBalance: totalValue,
      monthlyContrib: contrib,
      annualReturn: scenario.return,
      annualVolatility: scenario.vol,
      years,
      simulations: 500,
    });
  }, [totalValue, contrib, scenario, years]);

  // Run all 3 scenarios for outcome cards (lighter, 200 sims)
  const allScenarios = useMemo(() => {
    if (totalValue <= 0 && contrib <= 0) return null;
    return SCENARIOS.map(s => ({
      ...s,
      result: runMonteCarlo({
        currentBalance: totalValue,
        monthlyContrib: contrib,
        annualReturn: s.return,
        annualVolatility: s.vol,
        years,
        simulations: 200,
      }),
    }));
  }, [totalValue, contrib, years]);

  const hasData = totalValue > 0 || contrib > 0;

  // Chart data — shaded band between p25 and p75, plus median line
  const chartData = useMemo(() => {
    if (!simResult) return null;
    const yb = simResult.yearByYear;
    const labels = yb.map((_, i) => `Y${i}`);
    return {
      labels,
      datasets: [
        // Upper band fill (p75)
        {
          label: '75th pct',
          data: yb.map(y => y.p75),
          borderWidth: 0,
          pointRadius: 0,
          fill: '+1',
          backgroundColor: `${scenario.color}22`,
          tension: 0.4,
        },
        // Lower band (p25) — fills down to it from above
        {
          label: '25th pct',
          data: yb.map(y => y.p25),
          borderWidth: 0,
          pointRadius: 0,
          fill: false,
          tension: 0.4,
        },
        // Median line
        {
          label: 'Median',
          data: yb.map(y => y.median),
          borderColor: scenario.color,
          borderWidth: 2,
          pointRadius: 0,
          fill: false,
          tension: 0.4,
        },
        // p10 pessimistic dashed
        {
          label: 'Bear (p10)',
          data: yb.map(y => y.p10),
          borderColor: '#94a3b855',
          borderWidth: 1,
          borderDash: [4, 4],
          pointRadius: 0,
          fill: false,
          tension: 0.4,
        },
      ],
    };
  }, [simResult, scenario.color]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: '#1e293b',
        borderColor: '#334155',
        borderWidth: 1,
        titleColor: '#94a3b8',
        bodyColor: '#cbd5e1',
        callbacks: {
          title: (items) => `Year ${items[0].dataIndex}`,
          label: (item) => {
            if (item.dataset.label === '75th pct') return ` Bull: ${fmtC(item.raw)}`;
            if (item.dataset.label === '25th pct') return ` Bear (p25): ${fmtC(item.raw)}`;
            if (item.dataset.label === 'Median')   return ` Median: ${fmtC(item.raw)}`;
            if (item.dataset.label === 'Bear (p10)') return ` Bear (p10): ${fmtC(item.raw)}`;
            return '';
          },
        },
      },
    },
    scales: {
      x: {
        grid: { color: '#1e293b' },
        ticks: { color: '#475569', font: { size: 10 }, maxTicksLimit: 7 },
      },
      y: {
        grid: { color: '#1e293b' },
        ticks: {
          color: '#475569',
          font: { size: 10 },
          callback: (v) => fmtC(v),
        },
      },
    },
  };

  return (
    <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h2 className="font-semibold text-slate-200 text-base">Investment Projection</h2>
          <p className="text-xs text-slate-500 mt-0.5">Monte Carlo · 500 simulations</p>
        </div>
        {totalValue > 0 && (
          <div className="text-right flex-shrink-0">
            <div className="text-xs text-slate-500 mb-0.5">Starting value</div>
            <div className="text-sm font-bold text-slate-200">{fmt(totalValue)}</div>
          </div>
        )}
      </div>

      {!hasData ? (
        <div className="text-center py-8">
          <div className="text-3xl mb-3">📈</div>
          <p className="text-slate-400 text-sm mb-4 leading-relaxed">
            Add investment accounts or set a budget to see growth projections.
          </p>
        </div>
      ) : (
        <>
          {/* Controls row */}
          <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
            {/* Horizon pills */}
            <div className="flex gap-1.5">
              {HORIZONS.map((h, i) => (
                <button
                  key={h.label}
                  onClick={() => setHorizonIdx(i)}
                  className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
                    horizonIdx === i
                      ? 'bg-blue-600 text-white'
                      : 'bg-slate-700 text-slate-400 hover:bg-slate-600 hover:text-slate-200'
                  }`}
                >
                  {h.label}
                </button>
              ))}
            </div>
            {/* Scenario pills */}
            <div className="flex gap-1.5">
              {SCENARIOS.map(s => (
                <button
                  key={s.key}
                  onClick={() => setScenarioKey(s.key)}
                  className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
                    scenarioKey === s.key
                      ? 'text-slate-900 font-semibold'
                      : 'bg-slate-700 text-slate-400 hover:bg-slate-600 hover:text-slate-200'
                  }`}
                  style={scenarioKey === s.key ? { backgroundColor: s.color } : {}}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* Chart */}
          <div style={{ height: 200 }} className="mb-4">
            {chartData && <Line data={chartData} options={chartOptions} />}
          </div>

          {/* Outcome cards — all 3 scenarios at selected horizon */}
          {allScenarios && (
            <div className="flex gap-3 mb-4">
              {allScenarios.map(s => (
                <OutcomeCard
                  key={s.key}
                  label={s.label}
                  pessimistic={s.result.percentile10}
                  median={s.result.median}
                  optimistic={s.result.percentile90}
                  color={s.color}
                />
              ))}
            </div>
          )}

          {/* Stats row */}
          <div className="grid grid-cols-3 gap-3 text-center">
            <div className="bg-slate-700/40 rounded-lg p-3">
              <div className="text-[11px] text-slate-500 mb-1">Bear (p10)</div>
              <div className="text-sm font-bold text-rose-400 tabular-nums">
                {simResult ? fmtC(simResult.percentile10) : '—'}
              </div>
            </div>
            <div className="bg-slate-700/40 rounded-lg p-3">
              <div className="text-[11px] text-slate-500 mb-1">Median</div>
              <div className="text-sm font-bold tabular-nums" style={{ color: scenario.color }}>
                {simResult ? fmtC(simResult.median) : '—'}
              </div>
            </div>
            <div className="bg-slate-700/40 rounded-lg p-3">
              <div className="text-[11px] text-slate-500 mb-1">Bull (p90)</div>
              <div className="text-sm font-bold text-emerald-400 tabular-nums">
                {simResult ? fmtC(simResult.percentile90) : '—'}
              </div>
            </div>
          </div>

          {/* Monthly contrib note */}
          {contrib > 0 && (
            <p className="text-xs text-slate-600 mt-3">
              Assumes {fmt(contrib)}/mo in additional contributions based on your budget savings rate.
            </p>
          )}

          {/* Footer */}
          <div className="mt-4 pt-3 border-t border-slate-700/40 flex items-center justify-between">
            <span className="text-xs text-slate-600">
              {scenario.return}% expected return · {scenario.vol}% volatility · inflation not shown
            </span>
            <button
              onClick={() => {/* handled by parent view switch */}}
              className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-0.5 font-medium transition-colors"
            >
              Run simulator <ArrowRight size={11} />
            </button>
          </div>
        </>
      )}
    </div>
  );
}
